import { ClipboardEvent, FormEvent, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, FormControl, InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography } from '@mui/material';
import axios from '@/services/api/axios';
import { dictionariesService } from '@/services';

type Currency = { id: string; iso_code: string };
type News = { id:string; title:string; body:string; published_at:string; target_currency_id:string|null; is_active:boolean; attachments:{id:string;filename:string}[] };

const errorText = (error: any) => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((item) => `${item.loc?.at(-1) || 'Поле'}: ${item.msg}`).join('; ');
  return typeof detail === 'string' ? detail : 'Не удалось опубликовать новость';
};

export default function NewsBroadcast() {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [geo, setGeo] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [news, setNews] = useState<News[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => setNews((await axios.get('/news')).data);
  useEffect(() => { void dictionariesService.getCurrencies().then(setCurrencies); void load(); }, []);

  const paste = (event: ClipboardEvent<HTMLDivElement>) => {
    const pasted = Array.from(event.clipboardData.files);
    if (pasted.length) { event.preventDefault(); setFiles((current) => [...current, ...pasted]); }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault(); setError(''); setSuccess('');
    const data = new FormData();
    data.append('title', title.trim()); data.append('body', body.trim());
    if (geo) data.append('target_currency_id', geo);
    files.forEach((file) => data.append('attachments', file, file.name));
    setSending(true);
    try {
      await axios.post('/news', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setTitle(''); setBody(''); setGeo(''); setFiles([]); setSuccess('Новость опубликована'); await load();
    } catch (requestError) { setError(errorText(requestError)); }
    finally { setSending(false); }
  };

  return <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
    <Typography variant="h4" mb={.5}>Рассылка новостей</Typography>
    <Typography color="text.secondary" mb={3}>Публикация объявлений для всех трейдеров или выбранного GEO.</Typography>
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 4 }} onPaste={paste}>
      <form onSubmit={submit}>
        <TextField fullWidth label="Заголовок" value={title} onChange={(e) => setTitle(e.target.value)} required sx={{ mb: 2 }} />
        <TextField fullWidth multiline minRows={6} label="Текст новости" value={body} onChange={(e) => setBody(e.target.value)} required sx={{ mb: 2 }} />
        <FormControl fullWidth sx={{ mb: 2 }}><InputLabel>GEO</InputLabel><Select label="GEO" value={geo} onChange={(e) => setGeo(e.target.value)}><MenuItem value="">Все GEO</MenuItem>{currencies.map((currency) => <MenuItem key={currency.id} value={currency.id}>{currency.iso_code}</MenuItem>)}</Select></FormControl>
        <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 2, p: 2.5, mb: 2, bgcolor: 'background.default' }}>
          <Button variant="outlined" component="label">Прикрепить файлы<input hidden type="file" multiple accept="image/*,video/*,audio/*,.pdf,.docx,.zip,.txt" onChange={(e) => setFiles((current) => [...current, ...Array.from(e.target.files || [])])} /></Button>
          <Typography variant="body2" color="text.secondary" mt={1}>Также можно вставить изображение или файл из буфера обмена через Ctrl+V.</Typography>
          <Stack direction="row" gap={1} flexWrap="wrap" mt={2}>{files.map((file, index) => <Chip key={`${file.name}-${index}`} label={file.name} onDelete={() => setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))} />)}</Stack>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        <Button type="submit" variant="contained" disabled={sending || !title.trim() || !body.trim()}>{sending ? 'Отправка…' : 'Опубликовать'}</Button>
      </form>
    </Paper>
    <Typography variant="h5" mb={2}>История публикаций</Typography>
    {news.map((item) => <Paper variant="outlined" key={item.id} sx={{ p: 2.5, mb: 2, opacity: item.is_active ? 1 : .55 }}><Stack direction="row" justifyContent="space-between" gap={2}><Typography variant="h6">{item.title}</Typography><Chip size="small" label={item.target_currency_id ? (currencies.find((currency) => currency.id === item.target_currency_id)?.iso_code || 'GEO') : 'Все GEO'} /></Stack><Typography sx={{ whiteSpace: 'pre-wrap', my: 1.5 }}>{item.body}</Typography><Typography variant="caption" color="text.secondary">{new Date(item.published_at).toLocaleString()}</Typography>{item.attachments.map((attachment) => <Typography variant="caption" display="block" key={attachment.id}>📎 {attachment.filename}</Typography>)}{item.is_active && <Button size="small" color="error" sx={{ mt: 1 }} onClick={async () => { await axios.delete(`/news/${item.id}`); await load(); }}>Снять с публикации</Button>}</Paper>)}
  </Box>;
}
